import { useEffect, useRef } from 'react';
import { useDropGenStore } from '../stores/dropgenStore';
import type { Product } from '../stores/dropgenStore';

interface SocketMessage {
  type: 'research_update' | 'research_complete' | 'chat_message' | 'error';
  data: any;
}

export function useDropGenSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);
  const {
    setWsConnected,
    updateAgent,
    completeResearch,
    addMessage,
    updateResearch,
    loadResearchHistory,
    loadProducts,
    currentResearch,
  } = useDropGenStore();

  useEffect(() => {
    let mounted = true;

    const connect = () => {
      if (!mounted) return;

      try {
        const wsUrl = process.env.NODE_ENV === 'production'
          ? `wss://${window.location.host}/ws/dropgen`
          : 'ws://localhost:8780/ws';

        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('[DropGen] WebSocket connected');
          setWsConnected(true);
          reconnectAttemptsRef.current = 0;
        };

        ws.onmessage = (event) => {
          try {
            const message: SocketMessage = JSON.parse(event.data);
            handleMessage(message);
          } catch (err) {
            console.error('[DropGen] Failed to parse message:', err);
          }
        };

        ws.onerror = (error) => {
          console.error('[DropGen] WebSocket error:', error);
        };

        ws.onclose = () => {
          console.log('[DropGen] WebSocket closed');
          setWsConnected(false);
          wsRef.current = null;

          // Exponential backoff for reconnection
          if (mounted) {
            reconnectAttemptsRef.current += 1;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log(`[DropGen] Attempting to reconnect (attempt ${reconnectAttemptsRef.current})...`);
              connect();
            }, delay);
          }
        };

        wsRef.current = ws;
      } catch (err) {
        console.error('[DropGen] Failed to create WebSocket:', err);
        setWsConnected(false);

        // Retry connection
        if (mounted) {
          reconnectTimeoutRef.current = setTimeout(connect, 5000);
        }
      }
    };

    const handleMessage = (message: SocketMessage) => {
      switch (message.type) {
        case 'research_update':
          handleResearchUpdate(message.data);
          break;
        case 'research_complete':
          handleResearchComplete(message.data);
          break;
        case 'chat_message':
          handleChatMessage(message.data);
          break;
        case 'error':
          handleError(message.data);
          break;
        default:
          console.warn('[DropGen] Unknown message type:', message.type);
      }
    };

    // Map orchestrator agent names to UI agent names
    const agentNameMap: Record<string, string> = {
      'orchestrator': 'Market Analyst',
      'market': 'Market Analyst',
      'scout': 'Product Scout 1',
      'scout1': 'Product Scout 1',
      'scout2': 'Product Scout 2',
      'scout3': 'Product Scout 3',
      'trends': 'Trend Forecaster',
      'profit': 'Profit Calculator',
      'synthesis': 'Synthesis Agent',
    };

    const handleResearchUpdate = (data: {
      research_id: string;
      status?: string;
      progress?: number;
      message?: string;
      agent?: string;
    }) => {
      if (data.progress !== undefined) {
        updateResearch({ progress: data.progress });
      }

      // Map status to UI expected values
      if (data.status === 'researching' || data.status === 'running' || data.status === 'started') {
        updateResearch({ status: 'researching' });
      } else if (data.status === 'completed') {
        updateResearch({ status: 'completed' });
      }

      if (data.agent) {
        const uiAgentName = agentNameMap[data.agent] || data.agent;
        const agentStatus = data.status === 'completed' ? 'completed' :
                           data.status === 'running' || data.status === 'started' ? 'running' : 'idle';
        updateAgent(uiAgentName, { status: agentStatus });
      }

      if (data.message) {
        addMessage('system', data.message);
      }
    };

    const handleResearchComplete = (data: {
      research_id: string;
      status: string;
      exit_code: number;
    }) => {
      const status = data.status?.toUpperCase();
      if (status === 'COMPLETED' || data.exit_code === 0) {
        updateResearch({ status: 'completed', progress: 100 });
        addMessage('system', 'Research completed successfully!');
        // Reset all agents to idle
        Object.keys(agentNameMap).forEach(key => {
          updateAgent(agentNameMap[key], { status: 'idle' });
        });
        // Load products for the completed research
        loadProducts(data.research_id);
        // Refresh history to show the new completed research
        loadResearchHistory();
      } else {
        updateResearch({ status: 'error' });
        addMessage('system', `Research failed with exit code ${data.exit_code}`);
        // Still refresh history to update status
        loadResearchHistory();
      }
    };

    const handleChatMessage = (data: {
      research_id: string;
      role: 'user' | 'assistant' | 'system';
      content: string;
    }) => {
      const sender = data.role === 'assistant' ? 'dropgen' : data.role;
      addMessage(sender as any, data.content);
    };

    const handleError = (data: { message: string }) => {
      addMessage('system', `Error: ${data.message}`);
      updateResearch({ status: 'error' });
    };

    // Initial connection
    connect();

    // Cleanup
    return () => {
      mounted = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [
    setWsConnected,
    updateAgent,
    completeResearch,
    addMessage,
    updateResearch,
    loadResearchHistory,
    loadProducts,
  ]);

  return {
    send: (message: any) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(message));
      } else {
        console.warn('[DropGen] WebSocket not connected, cannot send message');
      }
    },
  };
}
